import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "./router";
import { AppShell } from "./components/AppShell";
import { Brand } from "./components/Brand";
import { ErrorState, LoadingState } from "./components/ui";
import { IntroTour } from "./components/IntroTour";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { RealtimeProvider } from "./realtime/RealtimeProvider";
import { NativeBridge } from "./native/NativeBridge";

const LoginPage = lazy(() =>
  import("./pages/auth").then((module) => ({ default: module.LoginPage })),
);
const InviteRegistrationPage = lazy(() =>
  import("./pages/auth").then((module) => ({ default: module.InviteRegistrationPage })),
);
const PasswordResetPage = lazy(() =>
  import("./pages/auth").then((module) => ({ default: module.PasswordResetPage })),
);
const AccountStatusPage = lazy(() =>
  import("./pages/auth").then((module) => ({ default: module.AccountStatusPage })),
);
const BootstrapPage = lazy(() =>
  import("./pages/auth").then((module) => ({ default: module.BootstrapPage })),
);
const HomePage = lazy(() =>
  import("./pages/dashboard").then((module) => ({ default: module.HomePage })),
);
const ActiveGamesPage = lazy(() =>
  import("./pages/dashboard").then((module) => ({ default: module.ActiveGamesPage })),
);
const LeaderboardPage = lazy(() =>
  import("./pages/dashboard").then((module) => ({ default: module.LeaderboardPage })),
);
const PublicProfilePage = lazy(() =>
  import("./pages/dashboard").then((module) => ({ default: module.PublicProfilePage })),
);
const GamesPage = lazy(() =>
  import("./pages/games").then((module) => ({ default: module.GamesPage })),
);
const GameDetailPage = lazy(() =>
  import("./pages/games").then((module) => ({ default: module.GameDetailPage })),
);
const TablePage = lazy(() =>
  import("./pages/games").then((module) => ({ default: module.TablePage })),
);
const MarketplacePage = lazy(() =>
  import("./pages/economy").then((module) => ({ default: module.MarketplacePage })),
);
const TradePage = lazy(() =>
  import("./pages/economy").then((module) => ({ default: module.TradePage })),
);
const PublicLogPage = lazy(() =>
  import("./pages/community").then((module) => ({ default: module.PublicLogPage })),
);
const VotesPage = lazy(() =>
  import("./pages/community").then((module) => ({ default: module.VotesPage })),
);
const AccountPage = lazy(() =>
  import("./pages/account").then((module) => ({ default: module.AccountPage })),
);
const InventoryPage = lazy(() =>
  import("./pages/account").then((module) => ({ default: module.InventoryPage })),
);
const NotificationSettingsPage = lazy(() =>
  import("./pages/account").then((module) => ({ default: module.NotificationSettingsPage })),
);
const SessionsPage = lazy(() =>
  import("./pages/account").then((module) => ({ default: module.SessionsPage })),
);
const WalletPage = lazy(() =>
  import("./pages/account").then((module) => ({ default: module.WalletPage })),
);
const AdminDashboardPage = lazy(() =>
  import("./pages/admin").then((module) => ({ default: module.AdminDashboardPage })),
);
const AdminUsersPage = lazy(() =>
  import("./pages/admin").then((module) => ({ default: module.AdminUsersPage })),
);
const AdminConsolePage = lazy(() =>
  import("./pages/admin").then((module) => ({ default: module.AdminConsolePage })),
);
const ServerStatusPage = lazy(() =>
  import("./pages/admin").then((module) => ({ default: module.ServerStatusPage })),
);
const FlairEditorPage = lazy(() =>
  import("./pages/admin").then((module) => ({ default: module.FlairEditorPage })),
);
const TournamentsPage = lazy(() =>
  import("./pages/admin").then((module) => ({ default: module.TournamentsPage })),
);
const TeamPage = lazy(() =>
  import("./pages/admin").then((module) => ({ default: module.TeamPage })),
);
const LegalPage = lazy(() =>
  import("./pages/public").then((module) => ({ default: module.LegalPage })),
);
const AndroidDownloadPage = lazy(() =>
  import("./pages/public").then((module) => ({ default: module.AndroidDownloadPage })),
);
const NotFoundPage = lazy(() =>
  import("./pages/public").then((module) => ({ default: module.NotFoundPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (attempt, error) => {
        const status =
          typeof error === "object" && error !== null && "status" in error
            ? Number(error.status)
            : 0;
        return status >= 400 && status < 500 ? false : attempt < 2;
      },
      refetchOnWindowFocus: true,
    },
  },
});

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Frontend boundary", error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="fatal-error">
          <Brand />
          <ErrorState
            title="Die Oberfläche konnte nicht geladen werden"
            message="Es wurden keine Aktionen ausgeführt. Lade die Anwendung neu oder melde den Fehler mit dem angezeigten Zeitpunkt an Jury."
            onRetry={() => window.location.reload()}
          />
          <time>{new Date().toLocaleString("de-DE", { timeZone: "Europe/Berlin" })}</time>
        </div>
      );
    }
    return this.props.children;
  }
}

function RoleRoute({ role, children }: { role: "ADMIN" | "TEAM"; children: ReactNode }) {
  const auth = useAuth();
  if (auth.state.kind !== "authenticated") return <Navigate to="/login" replace />;
  const allowed =
    role === "ADMIN"
      ? auth.state.viewer.roles.includes("ADMIN")
      : auth.state.viewer.roles.includes("ADMIN") || auth.state.viewer.roles.includes("MODERATOR");
  return allowed ? <>{children}</> : <Navigate to="/" replace />;
}

function AuthenticatedApplication() {
  return (
    <RealtimeProvider>
      <NativeBridge>
        <AppShell>
          <IntroTour>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/aktive-games" element={<ActiveGamesPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/profile/:id" element={<PublicProfilePage />} />
              <Route path="/games" element={<GamesPage />} />
              <Route path="/games/:gameId" element={<GameDetailPage />} />
              <Route path="/tische/:tableId" element={<TablePage />} />
              <Route path="/sessions/:sessionId" element={<TablePage sessionOnly />} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/marketplace/handel/:tradeId" element={<TradePage />} />
              <Route path="/abstimmungen" element={<VotesPage />} />
              <Route path="/log" element={<PublicLogPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/account/wallet" element={<WalletPage />} />
              <Route path="/account/inventar" element={<InventoryPage />} />
              <Route path="/account/inventar/:itemId" element={<InventoryPage />} />
              <Route path="/account/sitzungen" element={<SessionsPage />} />
              <Route path="/account/benachrichtigungen" element={<NotificationSettingsPage />} />
              <Route
                path="/team"
                element={
                  <RoleRoute role="TEAM">
                    <TeamPage />
                  </RoleRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <RoleRoute role="ADMIN">
                    <AdminDashboardPage />
                  </RoleRoute>
                }
              />
              <Route
                path="/admin/nutzer"
                element={
                  <RoleRoute role="ADMIN">
                    <AdminUsersPage />
                  </RoleRoute>
                }
              />
              <Route
                path="/admin/konsole"
                element={
                  <RoleRoute role="ADMIN">
                    <AdminConsolePage />
                  </RoleRoute>
                }
              />
              <Route
                path="/admin/status"
                element={
                  <RoleRoute role="ADMIN">
                    <ServerStatusPage />
                  </RoleRoute>
                }
              />
              <Route
                path="/admin/flairs"
                element={
                  <RoleRoute role="ADMIN">
                    <FlairEditorPage />
                  </RoleRoute>
                }
              />
              <Route
                path="/admin/turniere"
                element={
                  <RoleRoute role="ADMIN">
                    <TournamentsPage />
                  </RoleRoute>
                }
              />
              <Route
                path="/admin/turniere/:tournamentId"
                element={
                  <RoleRoute role="ADMIN">
                    <TournamentsPage />
                  </RoleRoute>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </IntroTour>
        </AppShell>
      </NativeBridge>
    </RealtimeProvider>
  );
}

function RouterContent() {
  const auth = useAuth();
  const location = useLocation();
  const publicPath =
    location.pathname === "/login" ||
    location.pathname.startsWith("/einladung/") ||
    location.pathname.startsWith("/passwort-reset/") ||
    location.pathname.startsWith("/rechtliches") ||
    location.pathname === "/android";

  if (publicPath) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/einladung/:token" element={<InviteRegistrationPage />} />
        <Route path="/passwort-reset/:token" element={<PasswordResetPage />} />
        <Route path="/rechtliches" element={<LegalPage />} />
        <Route path="/rechtliches/:page" element={<LegalPage />} />
        <Route path="/android" element={<AndroidDownloadPage />} />
      </Routes>
    );
  }

  if (auth.state.kind === "loading") {
    return (
      <div className="boot-screen">
        <Brand />
        <LoadingState label="Sichere Sitzung wird geprüft …" rows={2} />
      </div>
    );
  }
  if (auth.state.kind === "unavailable") {
    return (
      <div className="boot-screen">
        <Brand />
        <ErrorState
          title="F‑Bucks ist nicht erreichbar"
          message={auth.state.message}
          onRetry={() => void auth.refresh()}
        />
      </div>
    );
  }
  if (auth.state.kind === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (auth.state.viewer.status !== "ACTIVE") {
    return <AccountStatusPage viewer={auth.state.viewer} />;
  }
  if (auth.state.viewer.setupSteps.length > 0) {
    return location.pathname === "/setup" ? <BootstrapPage /> : <Navigate to="/setup" replace />;
  }
  return <AuthenticatedApplication />;
}

export default function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Suspense
              fallback={
                <div className="boot-screen">
                  <Brand />
                  <LoadingState label="Oberfläche wird geladen …" rows={2} />
                </div>
              }
            >
              <RouterContent />
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
